import {
  ApiProject,
  ApiReportResponse,
  ApiTabChartResponse,
  ApiTabResponse,
  ApiChartYAxisKey,
} from "@incendium/api";
import { HelpOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  DrawerProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { chartService, reportService } from "Apis";
import { parseContextChartFromTabChart, useCharts } from "Hooks/useCharts";
import { IChart } from "Interfaces";
import moment from "moment";
import { useReportBuilderContext } from "Providers/ReportBuilderProvider";
import { memo, useCallback, useEffect } from "react";
import { useState } from "react";
import EditReportChart from "./EditReportChart";
import Loading from "Components/Loading/Loading";
import { AnimatePresence } from "motion/react";
import StyledDrawer, { StyledDrawerTitle } from "Components/UI/StyledDrawer";
import { DatePickerDropdown } from "features/analytics";
import { useNotification } from "Hooks/useNotification";
import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";

interface IReportTabChartDrawerProps extends DrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  project: ApiProject;
  report: ApiReportResponse;
  tab: ApiTabResponse;
  tabChart?: ApiTabChartResponse;
  reload: () => void;
}

function ReportTabChartDrawer({
  open,
  setOpen,
  project,
  report,
  tab,
  tabChart,
  reload,
}: IReportTabChartDrawerProps) {
  const theme = useTheme();
  const { chart, setChart } = useReportBuilderContext();
  const [loadedTabChart, setLoadedTabChart] = useState<ApiTabChartResponse>({});
  const { refetch } = useCharts();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const load = useCallback(async () => {
    if (!tabChart) {
      return;
    }
    const res = await reportService.reportServiceReadTabChart({
      projectId: project?.id as number,
      reportId: report.id as number,
      tabId: tab.id as number,
      chartId: tabChart.id as number,
    });

    setLoadedTabChart(res);
  }, [tabChart]);

  useEffect(() => {
    if (!tabChart) {
      return;
    }

    load();
  }, [tabChart, load]);

  useEffect(() => {
    if (!loadedTabChart || !loadedTabChart?.chart) {
      return;
    }
    setChart(parseContextChartFromTabChart(loadedTabChart));
  }, [loadedTabChart]);

  const createChart = async () => {
    onSave(async () => {
      if (!tabChart) {
        return;
      }
      const newChartRes = await chartService.chartServiceCreateChart({
        projectId: project?.id as number,
        chartId: chart.id as number,
        payload: {
          ...chart,
          yAxisKeys: (chart.yAxisKeys || []).filter(
            (v) => typeof v !== "string",
          ) as ApiChartYAxisKey[], // filter should not be needed but here yo guarantee type
        },
      });

      const res = await reportService.reportServiceUpdateTabChart({
        projectId: project.id as number,
        reportId: report.id as number,
        tabId: tab.id as number,
        chartId: tabChart.id as number,
        payload: {
          chartId: newChartRes.id,
          removeOverridingChart: true, //wipe overriding
        },
      });

      setLoadedTabChart(res);
    }, `${chart.name} Saved`);
  };
  const updateChart = async () => {
    onSave(
      () =>
        chartService.chartServiceUpdateChart({
          projectId: project?.id as number,
          chartId: chart.id as number,
          payload: {
            ...chart,
            yAxisKeys: (chart.yAxisKeys || []).filter(
              (v) => typeof v !== "string",
            ) as ApiChartYAxisKey[], // filter should not be needed but here yo guarantee type
          },
        }),
      `${chart.name} Saved`,
    );
  };

  const saveInstance = async () => {
    onSave(async () => {
      // compare fields of eited chart and original chart.
      // all changed fields get appened to override chart in tabChart
      if (!loadedTabChart?.chart || !tabChart) {
        return;
      }
      const originalChart = parseContextChartFromTabChart(loadedTabChart.chart);
      const changed = Object.fromEntries(
        Object.entries(chart).filter(([key, value]) => {
          return (
            JSON.stringify(value) !==
            JSON.stringify(originalChart[key as keyof IChart])
          );
        }),
      );
      const res = await reportService.reportServiceUpdateTabChart({
        projectId: project.id as number,
        reportId: report.id as number,
        tabId: tab.id as number,
        chartId: tabChart.id as number,
        payload: {
          ...loadedTabChart,
          chartId: chart.id,
          overridingChart: {
            ...changed,
          },
        },
      });

      setLoadedTabChart(res);
    }, `${chart.name} Saved`);
  };

  const onSave = async (fn: () => void, success: string) => {
    try {
      await fn();
      setOpen(false);
      reload();
      refetch();
      showSuccessNotification(success);
    } catch (error) {
      showErrorNotification("Something Went Wrong");
    }
  };

  return (
    <StyledDrawer anchor="right" open={open} onClose={() => setOpen(false)}>
      <StyledDrawerTitle>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"center"}
        >
          <Typography variant="subtitle1">{tabChart?.chart?.name}</Typography>
          <DatePickerDropdown size="small" label="select date range" />
        </Stack>
      </StyledDrawerTitle>
      <Stack
        sx={{
          height: `calc(100vh - ${TOP_BAR_HEIGHT}px - 74px)`,
          width: "85vw",
        }}
      >
        <AnimatePresence>
          {!loadedTabChart || !loadedTabChart?.chart ? (
            <Loading />
          ) : (
            <>
              <Box
                sx={{
                  height: `calc(100vh - ${TOP_BAR_HEIGHT}px - 100px)`,
                  flex: 1,
                  width: "100%",
                }}
              >
                <EditReportChart loadedTabChart={loadedTabChart} />
              </Box>
              <Box
                px={3}
                py={2}
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack
                  direction={"row"}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={"center"}
                >
                  <Stack
                    direction={"row"}
                    spacing={2}
                    justifyContent="flex-start"
                  >
                    <Tooltip
                      arrow
                      title="Saves changes to chart, this will effect all uses of that chart in other reports."
                    >
                      <Button endIcon={<HelpOutline />} onClick={updateChart}>
                        Save Chart (Overwrite)
                      </Button>
                    </Tooltip>
                    <Tooltip
                      arrow
                      title="Saves as a new chart in the chart library, available for use in other reports."
                    >
                      <Button endIcon={<HelpOutline />} onClick={createChart}>
                        Save as New Chart
                      </Button>
                    </Tooltip>
                    <Tooltip
                      arrow
                      title="Saves as an override of this chart for this report only (not available in chart library). Does not affect the original chart and any other uses of it."
                    >
                      <Button endIcon={<HelpOutline />} onClick={saveInstance}>
                        Save Instance for This Report only
                      </Button>
                    </Tooltip>
                  </Stack>
                  {loadedTabChart &&
                    loadedTabChart.chart &&
                    loadedTabChart.chart.updatedAt && (
                      <Typography>
                        last saved on{" "}
                        {moment(loadedTabChart.chart.updatedAt).format("lll")}
                      </Typography>
                    )}
                </Stack>
              </Box>
            </>
          )}
        </AnimatePresence>
      </Stack>
    </StyledDrawer>
  );
}

export default memo(ReportTabChartDrawer);
